"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LabelDialogSectionValueInner = exports.LabelDialogSectionValue = void 0;
const tslib_1 = require("tslib");
const catalog_forms_1 = require("@tableau/catalog-forms");
const catalog_messages_1 = require("@tableau/catalog-messages");
const vizportalclient_common_components_1 = require("@tableau/vizportalclient-common-components");
const widgets_1 = require("@tableau/widgets");
const formik_1 = require("formik");
const react_1 = tslib_1.__importDefault(require("react"));
const LabelHelpTooltips_1 = require("../help/LabelHelpTooltips");
const LabelType_1 = require("../shared/LabelType");
const LabelDialogStyle_1 = require("./LabelDialogStyle");
const LabelsDialogContexts_1 = require("./LabelsDialogContexts");
const Placeholders_1 = require("./Placeholders");
const useCurrentValuesAsTabList_1 = require("./useCurrentValuesAsTabList");
const VerticalTabList_1 = require("./VerticalTabList");
exports.LabelDialogSectionValue = react_1.default.memo(function LabelDialogSectionValue() {
    const items = (0, useCurrentValuesAsTabList_1.useCurrentValuesAsTabList)();
    const state = (0, LabelsDialogContexts_1.useLabelDialogControlsState)();
    const category = state.kind === 'category' ? state.category : '';
    const filter = state.kind === 'filter' ? state.filter : '';
    const label = react_1.default.useMemo(() => {
        switch (state.kind) {
            case 'all':
                return (0, VerticalTabList_1.buildId)('category', 'tab', catalog_messages_1.Messages.AllLabels());
            case 'applied':
                return (0, VerticalTabList_1.buildId)('category', 'tab', catalog_messages_1.Messages.CustomLabelGridLabelValHeader());
            case 'category':
                return (0, VerticalTabList_1.buildId)('category', 'tab', (0, LabelType_1.localizeCategory)(category));
            case 'filter':
                return (0, VerticalTabList_1.buildId)('category', 'tab', filter);
        }
    }, [state.kind, category, filter]);
    return react_1.default.createElement(LabelDialogSectionValueInner, { label: label, items: items });
});
function LabelDialogSectionValueInner({ label, items }) {
    const dispatch = (0, LabelsDialogContexts_1.useLabelDialogControlsDispatcher)();
    const { value: current } = (0, LabelsDialogContexts_1.useLabelDialogControlsState)();
    const onActiveIndexChanged = react_1.default.useCallback((index) => {
        var _a, _b;
        const v = (_b = (_a = items[index]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
        if (v && v !== current) {
            dispatch({ type: 'SELECT_VALUE', value: v });
        }
    }, [dispatch, items, current]);
    const { element: tabList, reset } = (0, VerticalTabList_1.useVerticalTabList)({
        testId: `value-selector-${label}`,
        tabs: items.map(({ category, value, first }) => {
            return ({
                tabContent: {
                    heading: first ? react_1.default.createElement(Group, { category: category }) : undefined,
                    element: react_1.default.createElement(Row, { category: category, value: value }),
                    title: (0, LabelType_1.localizeLabelValueName)(value),
                    label: value,
                },
            });
        }),
        initialActiveIndex: 0,
        tabClassName: valueClass,
        ariaLabel: catalog_messages_1.Messages.LabelsDialog_ValueTitle(),
        onActiveIndexChanged: onActiveIndexChanged,
    });
    react_1.default.useEffect(() => {
        if (!current) {
            reset();
            onActiveIndexChanged(0);
        }
    }, [current, onActiveIndexChanged, reset]);
    return (react_1.default.createElement(LabelDialogStyle_1.LabelDialogBodySection, { "aria-labelledby": label, width: 280, role: 'tabpanel', style: {
            overflowX: 'hidden',
            borderRight: LabelDialogStyle_1.border,
            padding: 16,
            paddingTop: 12,
        } }, items.length > 0 ? tabList : react_1.default.createElement(Placeholders_1.Placeholders.Value, null)));
}
exports.LabelDialogSectionValueInner = LabelDialogSectionValueInner;
const valueClass = (0, vizportalclient_common_components_1.cssClass)('label-value-class', {
    margin: 2,
    paddingLeft: 8,
    border: '1px solid transparent',
    $nest: {
        '&:hover': {
            border: '1px solid #C9C9C9',
        },
        '&:focus': {
            border: '1px solid #2E2E2E',
        },
        '&[aria-selected="true"]': {
            margin: 3,
            border: '1px solid #2E2E2E',
            borderLeft: 'unset',
            boxShadow: 'inset 3px 0px #2E2E2E',
            paddingLeft: 7,
        },
        '&[aria-selected="true"]::hover': {
            border: '1px solid green',
            borderLeft: 'unset',
        },
    },
});
const ValueSelector = (0, vizportalclient_common_components_1.divWithStyle)('label-value-selector', {
    display: 'inline-flex',
    alignItems: 'center',
    paddingTop: 3,
    paddingBottom: 3,
});
const CheckBoxWrapper = (0, vizportalclient_common_components_1.divWithStyle)('label-value-checkbox', {
    marginRight: 3 * 2,
});
const ValueNameWrapper = (0, vizportalclient_common_components_1.divWithStyle)('label-value-name', {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: 202,
});
const GroupNameWrapper = (0, vizportalclient_common_components_1.divWithStyle)('label-category-group', {
    marginTop: 12,
    paddingBottom: 8,
    paddingLeft: 16,
});
function Group(props) {
    return react_1.default.createElement(GroupNameWrapper, null, (0, LabelType_1.localizeCategory)(props.category));
}
function Row(props) {
    const details = (0, LabelsDialogContexts_1.useLabelDialogDetails)().values[props.value];
    return details ? react_1.default.createElement(RowInner, { details: details }) : react_1.default.createElement(react_1.default.Fragment, null);
}
function RowInner({ details }) {
    var _a;
    const [field] = (0, formik_1.useField)(`${details.category}.${details.name}`);
    const elevated = (_a = field.value.elevated) !== null && _a !== void 0 ? _a : (details.elevatedDefault ? 'severe' : 'standard');
    useElevatedSynchronizeEffect(details.category, details.name, details.elevatedDefault ? 'severe' : 'standard');
    const state = (0, LabelsDialogContexts_1.useLabelDialogControlsState)();
    const active = state.value === details.name;
    const stickerProps = (0, LabelHelpTooltips_1.labelStickerProps)(details.category, elevated);
    const title = (0, LabelType_1.localizeCategoryAndLabel)(details.category, details.name);
    const text = (0, LabelType_1.localizeLabelValueName)(details.name);
    return (react_1.default.createElement(ValueSelector, null,
        react_1.default.createElement(CheckBoxWrapper, null,
            react_1.default.createElement(catalog_forms_1.FormikCheckBoxWidget, { tabIndex: active ? 0 : -1, config: { name: `${details.category}.${details.name}.active` } })),
        react_1.default.createElement(widgets_1.StickerWidget, Object.assign({}, stickerProps, { title: title, tabIndex: -1 }),
            react_1.default.createElement(ValueNameWrapper, null, text))));
}
function useElevatedSynchronizeEffect(category, value, elevatedDefault) {
    var _a;
    const { setFieldValue } = (0, LabelsDialogContexts_1.useLabelDialogState)();
    const [field, meta] = (0, formik_1.useField)(`${category}.${value}`);
    const enabled = field.value.active === true;
    const elevated = field.value.elevated;
    const initial = (_a = meta.initialValue) === null || _a === void 0 ? void 0 : _a.elevated;
    const path = `${category}.${value}.elevated`;
    react_1.default.useEffect(() => {
        if (enabled && elevated === undefined) {
            void setFieldValue(path, elevatedDefault, false);
        }
        else if (!enabled) {
            void setFieldValue(path, initial, false);
        }
    }, [elevated, elevatedDefault, enabled, initial, path, setFieldValue]);
}
//# sourceMappingURL=LabelsDialogSectionValue.js.map